import Page, { PageContentBanner, PageContentBodyGrid } from '../../Shared/Page/Page';
import { useEffect, useState, useCallback } from 'react';
import { toastDanger, toastInfo } from '../../../components/popups/Toast';
import { onDropBatch, useRemote } from '../../../Utils/Utils';
import { AlertCircle, CheckCircle, RefreshCw } from 'react-feather';
import { useWebsockets } from '../../../Utils/WebsocketReceiverHook';
import { useUploadWithConfirmation } from './DataUploadWithConfirmation';
import SearchTermFilter from '../../../components/Input/SearchTermFilter';
import DataUploadTable from './DataUploadTable';
import PageGridItem from '../../Shared/Page/PageGridItem';
import Button from '../../../components/Buttons/Button';
import LoadingIcon from '../../../components/LoadingIcon';
import SideBarExtensionHeader from '../../../components/SideBar/SideBarExtension/SideBarExtensionHeader';
import Divider from '../../../components/Divider';
import Modal from '../../../components/Modals/Modal';
import TextInput from '../../../components/Input/TextInput';
import useWindowDimensions from '../../../Utils/Utils';
import "./DataManagerPage.scss";


const DataManagerPage = () => {
    const [uploadsProcessing, setUploadsProcessing] = useState(null);
    const [filteredUploads, setFilteredUploads] = useState([]);
    const [hasSearchTerm, setHasSearchTerm] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [onHoldArray, setOnHoldArray] = useState([])
    const [showEncryptedDialog, setShowEncryptedDialog] = useState(false);
    const [batchWithEncryptedFiles, setBatchWithEncryptedFiles] = useState(null);
    const [passwords, setPasswords] = useState(null);
    const [superPassword, setSuperPassword] = useState('');
    const [unlockedFiles, setUnlockedFiles] = useState(null);
    const [unlockingAll, setUnlockingAll] = useState(false);
    const unfinishedArray = batchWithEncryptedFiles && batchWithEncryptedFiles.uploadedFiles.filter(f => f.status === 'ENCRYPTED');
    const completed = unfinishedArray && unfinishedArray.length === 0;

    const {
        checkBeforeUpload,
        ConfirmDialog,
    } = useUploadWithConfirmation();

    const remote = useRemote()
    const sockets = useWebsockets();
    const { height } = useWindowDimensions();

    const getUploads = useCallback(async function getUploads() {
        await remote.get(`data/listBatches`).then((response) => {
            const onHoldDetected = response.filter(r => {
                const foundFiles = r.uploadedFiles.filter(f => f.status === "ON_HOLD");
                return foundFiles && foundFiles.length > 0;
            });
            setOnHoldArray(onHoldDetected);
            setUploadsProcessing(response);
            setLoading(false)
        }).catch((error) => {
            setUploadsProcessing([])
            setLoading(false)
            console.error("Front end encountered an error getting the uploads: ", error);
        });
    }, [remote]);

    useEffect(() => {
        document.addEventListener('copy', realPasswordCopy);
        return () => document.removeEventListener('copy', realPasswordCopy);
    }, [])

    useEffect(() => {
        getUploads();
        sockets.subscribe({
            "/api/data/listBatches": [() => getUploads()]
        })
        return () => sockets.unsubscribe(["/api/data/listBatches"]);

    }, [getUploads]);

    function uploadData(batchId, fileName) {
        const reupload = !!(batchId && fileName);
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.style.display = 'none';
        fileInput.multiple = reupload ? false : true; // Allow multiple file selection
        fileInput.addEventListener('change', (event) => {
            if (event.target.files.length > 0) {
                let formData = new FormData()
                let uploaded = [];
                for (let i = 0; i < event.target.files.length; i++) {
                    uploaded.push(event.target.files[i].name);
                    formData.append(reupload ? 'file' : 'files', event.target.files[i]);
                }
                reupload
                    ? onDropBatch(formData, `/data/upload/reupload/${batchId}/${fileName}`, {
                        toastStartProcess: () => toastInfo("Uploading files", { autoClose: 5000 }),
                        onUploadComplete: onReUploadComplete,
                        onUploadError: (e, xhr) => {
                            toastDanger("Upload Error");
                        },
                        setLoading: setUploading
                    })
                    : onDropBatch(formData, `/data/upload/bulk`, {
                        toastStartProcess: () => toastInfo("Uploading files", { autoClose: 5000 }),
                        onUploadComplete: onUploadComplete,
                        onUploadError: (e, xhr) => {
                            toastDanger("Upload Error");
                        },
                        setLoading: setUploading
                    })
            }
        });

        document.body.appendChild(fileInput);
        fileInput.click();
    }

    function onUploadComplete(event, response, reload) {
        getUploads();
        const resp = response && JSON.parse(response);
        if (resp && resp.data) {
            resp.data?.success > 0 && toastInfo(
                <div>
                    <p style={{ fontWeight: 600, color: "#0E8EFD" }}>Files Processing</p>
                    <p style={{ color: "#0E8EFD" }}>Your files have been added to the queue. You'll receive an email once processing is complete.</p>
                </div>, { autoClose: 5000, className: 'toast-info-align-top' }
            );
            resp.data?.failed > 0 && toastDanger(`Failed to upload ${resp.data.failed} files`);
        } else if (resp && resp.success) {
            resp.success && toastInfo(
                <div>
                    <p style={{ fontWeight: 600, color: "#0E8EFD" }}>Files Processing</p>
                    <p style={{ color: "#0E8EFD" }}>Your files have been added to the queue. You'll receive an email once processing is complete.</p>
                </div>, { autoClose: 5000, className: 'toast-info-align-top' }
            );
        } else {
            toastDanger(`Failed to upload`)
        }
        const encryptedFiles = resp?.data?.ENCRYPTED
        if (encryptedFiles && encryptedFiles.length > 0) {
            checkEncrypted({ batchId: encryptedFiles[0].batchId, uploadedFiles: encryptedFiles }, setShowEncryptedDialog)
        }
    }

    function onReUploadComplete(event, response) {
        onUploadComplete(event, response, true)
    }

    const handleSendPassword = (password, currentBatch, file) => {
        if (file) {
            const dto = {
                batchId: currentBatch.batchId,
                fileId: file.fileId ? file.fileId : file.id,
                password: password,
                confirm: true
            }
            remote.post(`/data/upload/resume/${currentBatch.batchId}/decrypt`, [dto]).then(resp => {
                const updatedFileArray = resp.data["PENDING"] || resp.data["COMPLETED"] || resp.data["FAILED"] || resp.data["ENCRYPTED"];
                const updatedFile = { ...updatedFileArray[0], password: password };
                const updatedUploadedFiles = currentBatch.uploadedFiles.map(f => {
                    if (f.fileId ? f.fileId === updatedFile.fileId : f.id === updatedFile.fileId) {
                        return updatedFile
                    }
                    return f;
                });
                setBatchWithEncryptedFiles({ ...currentBatch, uploadedFiles: updatedUploadedFiles });
            })
        } else {
            setUnlockingAll(true);
            const dtoList = currentBatch.uploadedFiles.filter(f => f.status === 'ENCRYPTED').map(fl => {
                const currentPassword = passwords && passwords[fl.id ? fl.id : fl.fileId] ? passwords[fl.id ? fl.id : fl.fileId].password : null;
                const dto = {
                    batchId: currentBatch.batchId,
                    fileId: fl.fileId ? fl.fileId : fl.id,
                    password: currentPassword && currentPassword !== password ? currentPassword : password,
                    confirm: true
                }
                return dto
            })

            remote.post(`/data/upload/resume/${currentBatch.batchId}/decrypt`, dtoList).then(resp => {
                const unchangedFile = currentBatch.uploadedFiles.filter(f => f.status !== 'ENCRYPTED');
                const unlockedFiles = resp.data["PENDING"] && resp.data["COMPLETED"] ? [...resp.data["PENDING"], ...resp.data["COMPLETED"]] : resp.data["PENDING"] ? resp.data["PENDING"] : resp.data["COMPLETED"] ? resp.data["COMPLETED"] : [];
                const invalidPasswordFiles = resp.data["ENCRYPTED"] ? resp.data["ENCRYPTED"] : [];

                const changedFiles = unlockedFiles.concat(...invalidPasswordFiles).map(f => {
                    const currentPassword = passwords && passwords[f.fileId] ? passwords[f.fileId].password : null;
                    return { ...f, password: currentPassword, status: f.status === 'COMPLETED' ? 'PENDING' : f.status };
                });

                const updatedBatch = {
                    ...currentBatch, uploadedFiles: [...unchangedFile, ...changedFiles].sort((a, b) => {
                        const aId = a.fileId ? a.fileId : a.id;
                        const bId = b.fileId ? b.fileId : b.id;
                        return aId - bId;
                    })
                }

                setUnlockedFiles(unlockedFiles);
                setBatchWithEncryptedFiles(updatedBatch);
                setUnlockingAll(false);
            })
        };
    }

    const handleCancelOneFileUpload = (currentBatch, file) => {
        const dto = {
            batchId: currentBatch.batchId,
            fileId: file.fileId ? file.fileId : file.id,
            password: null,
            confirm: false
        }
        remote.post(`/data/upload/resume/${currentBatch.batchId}/decrypt`, [dto]).then(resp => {
            const encriptedFilesLeft = currentBatch.uploadedFiles.filter(f => f.status === 'ENCRYPTED' && (f.fileId ? f.fileId : f.id) !== (file.fileId ? file.fileId : file.id));
            if (encriptedFilesLeft.length === 0) {
                setShowEncryptedDialog(false);
                setBatchWithEncryptedFiles(null);
            } else {
                setBatchWithEncryptedFiles({ ...currentBatch, uploadedFiles: encriptedFilesLeft });
            }
        })
    };

    const handleClose = () => {
        setShowEncryptedDialog(false);
        setBatchWithEncryptedFiles(null);
        setPasswords(null);
        setSuperPassword('');
        setUnlockedFiles(null);
    }

    const checkEncrypted = (batch) => {
        const encrypted = batch.uploadedFiles.filter(file => file.status === 'ENCRYPTED');
        if (encrypted.length > 0) {
            setBatchWithEncryptedFiles(batch);
            setShowEncryptedDialog(true);
        } else {
            setBatchWithEncryptedFiles(null);
            setShowEncryptedDialog(false);
        }
    };

    function providePassword(pwd, id) {
        if (!passwords) {
            const pwdsObj = batchWithEncryptedFiles.uploadedFiles.filter(f => f.status === 'ENCRYPTED').reduce((acc, f) => { acc[f.id ? f.id : f.fileId] = { id: f.id ? f.id : f.fileId, password: '' }; return acc; }, {})
            setPasswords({ ...pwdsObj, [id]: { ...pwdsObj[id], password: pwd } })
        } else {
            setPasswords({ ...passwords, [id]: { ...passwords[id], password: pwd } })
        }
    }

    function provideSuperPassword(pwd) {
        setSuperPassword(pwd);
        if (!passwords) {
            const pwdsObj = batchWithEncryptedFiles.uploadedFiles.filter(f => f.status === 'ENCRYPTED').reduce((acc, f) => { acc[f.id ? f.id : f.fileId] = { id: f.id ? f.id : f.fileId, password: pwd }; return acc; }, {})
            setPasswords(pwdsObj);
        } else {
            const filesObject = batchWithEncryptedFiles.uploadedFiles.reduce((acc, f) => { acc[f.id ? f.id : f.fileId] = f; return acc; }, {})
            const passwordsArr = Object.values(passwords).map(p => p);
            const updatedPasswords = { ...passwords };
            passwordsArr.forEach((p) => {
                const file = filesObject[p.id];
                if (file.status === 'ENCRYPTED') {
                    p.password = pwd;
                    updatedPasswords[p.id].password = pwd;
                }
            })
            setPasswords(updatedPasswords);
        }
    }

    function realPasswordCopy(e) {
        if (e.target.tagName === 'INPUT' && e.target.type === 'text' && e.target.getAttribute('data-lpignore') === 'true') {
            const copyText = e.target.value;
            e.clipboardData.setData('text/plain', copyText);
            e.preventDefault();
        }
    }


    return <Page fixedBanner>
        <PageContentBanner gap="m" divider>
            <SideBarExtensionHeader header='Data Manager' icon={<RefreshCw className='colour-text-primary' />} style={{ marginBottom: 0, paddingRight: 0 }} noTabs />
            <Divider vertical height="24px" marginTop="0.25rem" />
            <SearchTermFilter
                placeholder="Search Data Manager"
                size="small"
                width="l"
                isClearable
                dataSet={uploadsProcessing}
                setFilteredData={setFilteredUploads}
                path={["uploadedDate", "uploadedBy.name", 'uploadedBy.email', "status.numberCompleted", "status.numberFailed", "status.numberOfFiles", "status.numberPending", "status.numberProcessing"]}
                hasSearchTerm={setHasSearchTerm} />
            <Button onClick={() => uploadData()} >Import Data</Button>
            {uploading && <LoadingIcon size={24} />}
        </PageContentBanner>
        <PageContentBodyGrid rowGap="xl" gridTemplateRows="1fr">
            <PageGridItem container col="1 / span 12">
                <DataUploadTable
                    data={hasSearchTerm ? filteredUploads : uploadsProcessing}
                    reupload={uploadData}
                    loading={loading}
                    onHoldArray={onHoldArray}
                    checkBeforeUpload={checkBeforeUpload}
                    checkEncrypted={checkEncrypted}
                    setShowEncryptedDialog={setShowEncryptedDialog}
                    batchWithEncryptedFiles={batchWithEncryptedFiles}
                    setBatchWithEncryptedFiles={setBatchWithEncryptedFiles}
                />
            </PageGridItem>
        </PageContentBodyGrid >
        <ConfirmDialog />
        {showEncryptedDialog &&
            <Modal
                header={`File Encrypted`}
                modalHeaderClass="with-border"
                handleClose={() => handleClose()}
                cancelButton={{ label: completed ? "Complete" : "Close", variant: completed ? "positive" : "secondary", noPadding: true, onClick: () => handleClose() }}
                confirmButton={null}
                buttonsClass="mt-m"
                modalStyle={{ minWidth: 320 }}
                modalContentStyle={{ maxHeight: `calc(${height}px - 16rem)`, overflowY: "auto", scrollbarWidth: "thin" }}
            >
                <p className='pb-l border-default-bottom'><span>The following </span>{batchWithEncryptedFiles?.uploadedFiles.length === 1 ? <span> file</span> : <span style={{ fontWeight: 600 }}>{batchWithEncryptedFiles?.uploadedFiles.length + " files"}</span>}<span>{batchWithEncryptedFiles?.uploadedFiles.length === 1 ? " is " : " are "}</span>password-protected. Enter the<span>{batchWithEncryptedFiles?.uploadedFiles.length === 1 ? " password " : " passwords "}</span>below to continue.</p>
                {batchWithEncryptedFiles?.uploadedFiles.length > 1 &&
                    <div className='p-m pb-l pt-l border-default-bottom' style={{ display: 'flex', flexDirection: 'column' }}>
                        <p className='pb-m'>Enter one password to attempt unlocking all files.</p>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            <TextInput
                                inputstyle={{ maxWidth: 270 }}
                                style={{ height: 32, width: 270 }}
                                showPasswordToggle={true}
                                size="small" width="l"
                                type="text"
                                placeHolder="Unlock all"
                                value={superPassword} 
                                onChange={(e) => provideSuperPassword(e.target.value)}
                                eyeIconStyle={{ top: '-1.5rem', left: 'calc(270px - 1.5rem)' }}
                                autoComplete="off"
                                onKeyDown={(e) => { if (e.key === 'Enter' && superPassword) { handleSendPassword(superPassword, batchWithEncryptedFiles, null) } }}
                            />
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <Button disabled={!superPassword} onClick={() => handleSendPassword(superPassword, batchWithEncryptedFiles, null)}>Unlock All</Button>
                            </div>
                            {unlockingAll && <LoadingIcon size={24} />}
                        </div>
                        {unlockedFiles ? unlockedFiles.length > 0
                            ? <p>
                                <CheckCircle size={24} color={"#56D1C3"} style={{ transform: 'translateY(6px)', paddingRight: '0.5rem' }} />
                                <span /*className='colour-text-positive-dark'*/>{`${unlockedFiles.length} ${unlockedFiles.length > 1 ? "files" : "file"} unlocked`}</span>
                            </p>
                            : <p>
                                <AlertCircle size={24} color={"#E66780"} style={{ transform: 'translateY(6px)', paddingRight: '0.5rem' }} />
                                <span /*className='colour-text-danger-dark'*/>0 files unlocked</span>
                            </p>
                            : ""
                        }
                    </div>
                }
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {batchWithEncryptedFiles?.uploadedFiles.map((file, idx) => {
                        const errorText = "Invalid password provided."
                        const errorMessage = passwords && passwords[file.id ? file.id : file.fileId] && passwords[file.id ? file.id : file.fileId].password !== '' && file.status === 'ENCRYPTED' && file.message === errorText ? errorText : null

                        return (
                            <div className='border-default-bottom flex-direction-col' style={{ padding: '1.5rem 1rem', gap: '1rem' }} key={file.fileName + "-" + idx}>
                                <p>
                                    {file.status === 'ENCRYPTED'
                                        ? <AlertCircle size={24} color={"#E66780"} style={{ transform: 'translateY(6px)', paddingRight: '0.5rem' }} />
                                        : <CheckCircle size={24} color={"#56D1C3"} style={{ transform: 'translateY(6px)', paddingRight: '0.5rem' }} />
                                    }
                                    <span style={{ textDecoration: 'underline', overflowWrap: 'break-word', wordBreak: 'break-all' }}>{`${file.fileName}`}</span>
                                    <span></span>. Enter password to continue processing the file.
                                </p>
                                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                    <TextInput
                                        inputstyle={{ maxWidth: 270 }}
                                        style={{ height: errorMessage ? 47 : 32, width: 270 }}
                                        showPasswordToggle={file.status === 'ENCRYPTED'}
                                        helperText={errorMessage}
                                        helperTextClass="invalid-message negative-top"
                                        invalid={!!errorMessage}
                                        disabled={file.status !== 'ENCRYPTED'}
                                        size="small" 
                                        width="l"
                                        // TextInput only masks via the eye toggle while showPasswordToggle is on; once the
                                        // file is unlocked that toggle goes away, so fall back to a native password field
                                        // to keep the stored password hidden (SUP-3763).
                                        type={file.status === 'ENCRYPTED' ? 'text' : 'password'}
                                        placeHolder="File password"
                                        value={passwords && passwords[file.id ? file.id : file.fileId]?.password} onChange={(e) => providePassword(e.target.value, file.id ? file.id : file.fileId)}
                                        eyeIconStyle={{ top: '-1.5rem', left: 'calc(270px - 1.5rem)' }}
                                        autoComplete="off"
                                        data-lpignore="true"
                                        data-form-type="other"
                                        onKeyDown={(e) => { if (e.key === 'Enter' && passwords && passwords[file.id ? file.id : file.fileId]?.password) { handleSendPassword(passwords[file.id ? file.id : file.fileId].password, batchWithEncryptedFiles, file) } }}
                                    />
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        {file.status !== 'ENCRYPTED' && <Button noHover variant={file.status !== 'ENCRYPTED' ? 'positive' : null} onClick={() => null}>Unlocked</Button>}
                                        {file.status === 'ENCRYPTED' && <Button disabled={!passwords || (passwords && !passwords[file.id ? file.id : file.fileId]?.password)} onClick={() => handleSendPassword(passwords[file.id ? file.id : file.fileId].password, batchWithEncryptedFiles, file)}>Unlock</Button>}
                                        {file.status === 'ENCRYPTED' && <Button variant="danger" onClick={() => handleCancelOneFileUpload(batchWithEncryptedFiles, file, setShowEncryptedDialog)}>Remove File</Button>}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </Modal>
        }
    </Page >
}

export default DataManagerPage;